import React from "react";
import {
  Hash,
  Building,
  Globe,
  User,
  Mail,
  Clock,
  ChevronDown,
} from "lucide-react";
import CharacterCounter from "./CharacterCounter";
import RequiredFieldMarker from "./RequiredFieldMarker";
import { COUNTRIES } from "./constants";

const FieldError = ({ message }) =>
  message ? <p className="field-error">{message}</p> : null;

const fieldIconClass = "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500";
const selectChevronClass = "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500";

const TIMEZONES = [
  { id: "America/New_York", label: "Eastern Time (ET)" },
  { id: "America/Chicago", label: "Central Time (CT)" },
  { id: "America/Denver", label: "Mountain Time (MT)" },
  { id: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { id: "America/Anchorage", label: "Alaska Time (AKT)" },
  { id: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { id: "America/Phoenix", label: "Arizona Time (MST, no DST)" },
];

const SiteFormFields = ({ values, errors, onChange, mode = "add" }) => (
  <>
    {/* Site Code */}
    <div>
      <label htmlFor="site_code" className="label">
        Site Code <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Hash className={fieldIconClass} size={16} />
        <input
          id="site_code"
          name="site_code"
          type="text"
          maxLength={4}
          value={values.site_code}
          onChange={onChange}
          placeholder="Enter site code"
          className={`input pl-10 ${errors.site_code ? "input-error" : ""}`}
        />
      </div>
      <CharacterCounter current={values.site_code} max={4} />
      <FieldError message={errors.site_code} />
    </div>

    {/* Institution */}
    <div>
      <label htmlFor="institution" className="label">
        Institution <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Building className={fieldIconClass} size={16} />
        <input
          id="institution"
          name="institution"
          type="text"
          maxLength={100}
          value={values.institution}
          onChange={onChange}
          placeholder="Enter institution name"
          className={`input pl-10 ${errors.institution ? "input-error" : ""}`}
        />
      </div>
      <CharacterCounter current={values.institution} max={100} />
      <FieldError message={errors.institution} />
    </div>

    {/* Country */}
    <div>
      <label htmlFor="country" className="label">
        Country <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Globe className={fieldIconClass} size={16} />
        <select
          id="country"
          name="country"
          value={values.country}
          onChange={onChange}
          className={`input pl-10 pr-9 appearance-none ${errors.country ? "input-error" : ""}`}
        >
          <option value="">Select a country</option>
          {COUNTRIES.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        <ChevronDown className={selectChevronClass} size={16} />
      </div>
      <FieldError message={errors.country} />
    </div>

    {/* Contact Person */}
    <div>
      <label htmlFor="contact_person" className="label">
        Contact Person <RequiredFieldMarker />
      </label>
      <div className="relative">
        <User className={fieldIconClass} size={16} />
        <input
          id="contact_person"
          name="contact_person"
          type="text"
          maxLength={100}
          value={values.contact_person}
          onChange={onChange}
          placeholder="Enter full name"
          className={`input pl-10 ${errors.contact_person ? "input-error" : ""}`}
        />
      </div>
      <p className="hint">
        Please enter the contact person’s full name as it should appear officially
      </p>
      <CharacterCounter current={values.contact_person} max={100} />
      <FieldError message={errors.contact_person} />
    </div>

    {/* Contact Email */}
    <div>
      <label htmlFor="email" className="label">
        Contact Email <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Mail className={fieldIconClass} size={16} />
        <input
          id="email"
          name="email"
          type="email"
          value={values.email}
          onChange={onChange}
          placeholder="Enter contact email"
          className={`input pl-10 ${errors.email ? "input-error" : ""}`}
        />
      </div>
      <FieldError message={errors.email} />
    </div>

    {/* Timezone */}
    <div>
      <label htmlFor="timezone" className="label">
        Timezone <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Clock className={fieldIconClass} size={16} />
        <select
          id="timezone"
          name="timezone"
          value={values.timezone}
          onChange={onChange}
          className={`input pl-10 pr-9 appearance-none ${errors.timezone ? "input-error" : ""}`}
        >
          <option value="">Select a timezone</option>
          {TIMEZONES.map((tz) => (
            <option key={tz.id} value={tz.id}>
              {tz.label}
            </option>
          ))}
        </select>
        <ChevronDown className={selectChevronClass} size={16} />
      </div>
      <p className="hint">
        Participant timezones are derived from this. The timezone is required
        to ensure proper scheduling and notifications in participant’s local time.
      </p>
      <FieldError message={errors.timezone} />
    </div>
  </>
);

export default SiteFormFields;
